/**
 * Cart Item Upgrade Handler
 * Manages quantity upgrades for bundle discounts in Shopify carts.
 */
(function() {
  'use strict';

  // Configuration will be set from data attributes on the cart drawer
  const getConfig = () => {
    const drawer = document.querySelector('[data-cart-upgrade-config]');
    if (!drawer) return null;

    return {
      animationDuration: parseInt(drawer.dataset.upgradeAnimationDuration) || 300,
      successDelay: parseInt(drawer.dataset.upgradeSuccessDelay) || 1500,
      bundleThreshold: parseInt(drawer.dataset.bundleThreshold) || 3,
      enableUpgrades: drawer.dataset.enableUpgrades !== 'false',
      autoHideOnThreshold: drawer.dataset.autoHideOnThreshold !== 'false'
    };
  };

  const state = {
    isProcessing: false,
    currentUpgrades: new Map()
  };

  function setButtonState(btn, status) {
    btn.classList.remove('upgrading', 'success', 'error');

    if (status === 'upgrading') {
      btn.classList.add('upgrading');
    } else if (status === 'success') {
      btn.classList.add('success');
    } else if (status === 'error') {
      btn.classList.add('error');
    }

    btn.disabled = (status === 'upgrading' || status === 'success');

    if (status === 'success') {
      const config = getConfig();
      if (config) {
        setTimeout(() => setButtonState(btn, 'default'), config.successDelay);
      }
    }
  }

  function getCartSectionId() {
    const component = document.querySelector('cart-items-component[data-section-id]');
    return component ? component.dataset.sectionId : null;
  }

  function clearCartCaches() {
    ['cartCache', 'cart', 'cartData'].forEach(key => {
      if (window[key]) window[key] = null;
    });

    if (window.theme) {
      ['cart', 'cartCache', 'cartData'].forEach(key => {
        if (window.theme[key]) window.theme[key] = null;
      });
    }

    try {
      ['cart', 'cartData', 'cartCache'].forEach(key => {
        sessionStorage.removeItem(key);
      });
    } catch(e) {}
  }

  function dispatchCartEvents(source = 'upgrade') {
    const events = [
      'cart:update',
      'cart:refresh',
      'cart:change',
      'theme:cart:update',
      'theme:cart:add'
    ];

    events.forEach(eventName => {
      try {
        document.dispatchEvent(new CustomEvent(eventName, {
          detail: { source },
          bubbles: true,
          cancelable: true
        }));
      } catch(e) {
        console.debug(`Event dispatch failed: ${eventName}`, e);
      }
    });
  }

  function updateCartSections(sections) {
    if (!sections) return;

    const sectionId = getCartSectionId();

    if (sectionId && sections[sectionId]) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections[sectionId], 'text/html');
      const newComponent = doc.querySelector('cart-items-component');
      const currentComponent = document.querySelector('cart-items-component');

      if (newComponent && currentComponent) {
        const scrollEl = document.querySelector('.cart-drawer__items');
        const scrollTop = scrollEl ? scrollEl.scrollTop : 0;

        currentComponent.replaceWith(newComponent);

        const newScrollEl = document.querySelector('.cart-drawer__items');
        if (newScrollEl) newScrollEl.scrollTop = scrollTop;

        if (window.customElements?.upgrade) {
          try {
            customElements.upgrade(newComponent);
          } catch(e) {}
        }
      }
    }

    if (sections['cart-icon-bubble']) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newBubble = doc.querySelector('.cart-bubble');

      if (newBubble) {
        document.querySelectorAll('.cart-bubble').forEach(bubble => {
          bubble.innerHTML = newBubble.innerHTML;
          bubble.removeAttribute('hidden');
          bubble.style.display = '';
        });
      }
    }
  }

  function animateUpgradeRemoval(upgradeElement, config) {
    return new Promise(resolve => {
      if (!upgradeElement) {
        resolve();
        return;
      }

      upgradeElement.classList.add('removing');

      setTimeout(() => {
        upgradeElement.remove();
        resolve();
      }, config.animationDuration);
    });
  }

  async function getTotalMittCount() {
    try {
      const response = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) return 0;

      const cart = await response.json();
      let count = 0;

      cart.items.forEach(item => {
        const title = (item.title || item.product_title || '').toLowerCase();
        if (title.includes('mitt')) {
          count += item.quantity;
        }
      });

      return count;
    } catch(e) {
      console.error('Failed to get mitt count:', e);
      return 0;
    }
  }

  async function handleUpgrade(e) {
    const config = getConfig();
    if (!config || !config.enableUpgrades) return;

    const btn = e.target.closest('[data-upgrade-button]');
    if (!btn || btn.disabled || state.isProcessing) return;

    e.preventDefault();
    e.stopPropagation();

    const itemKey = btn.dataset.itemKey;
    const newQuantity = parseInt(btn.dataset.newQuantity);

    if (!itemKey || !newQuantity) {
      console.error('Missing upgrade data');
      return;
    }

    if (state.currentUpgrades.has(itemKey)) return;
    state.currentUpgrades.set(itemKey, true);
    state.isProcessing = true;

    const upgradeContainer = btn.closest('.cart-item-upgrade');

    setButtonState(btn, 'upgrading');
    clearCartCaches();

    try {
      const sectionId = getCartSectionId();
      const sections = sectionId ?
        [sectionId, 'cart-icon-bubble'].join(',') :
        'cart-icon-bubble';

      const response = await fetch('/cart/change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: newQuantity,
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Failed to update quantity');
      }

      const data = await response.json();
      clearCartCaches();

      const totalMitts = await getTotalMittCount();
      setButtonState(btn, 'success');

      if (config.autoHideOnThreshold && totalMitts >= config.bundleThreshold) {
        await animateUpgradeRemoval(upgradeContainer, config);

        document.querySelectorAll('.cart-item-upgrade').forEach(el => {
          if (el !== upgradeContainer) {
            el.remove();
          }
        });
      }

      if (data.sections) {
        setTimeout(() => {
          updateCartSections(data.sections);
        }, (config.autoHideOnThreshold && totalMitts >= config.bundleThreshold) ? config.animationDuration + 50 : 50);
      }

      dispatchCartEvents('upgrade');

      if (window.updateAllProgress) {
        setTimeout(() => {
          window.updateAllProgress(true);
        }, 100);
      }

    } catch (error) {
      console.error('Upgrade error:', error);
      setButtonState(btn, 'error');

      const errorMsg = error.message || 'Failed to upgrade. Please try again.';

      if (window.theme?.showNotification) {
        window.theme.showNotification(errorMsg, 'error');
      } else {
        const originalText = btn.querySelector('.upgrade-text');
        if (originalText) {
          const tempText = originalText.textContent;
          originalText.textContent = 'Error';
          setTimeout(() => {
            originalText.textContent = tempText;
            setButtonState(btn, 'default');
          }, 2000);
        }
      }
    } finally {
      state.currentUpgrades.delete(itemKey);
      state.isProcessing = false;
    }
  }

  function init() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-upgrade-button]')) {
        handleUpgrade(e);
      }
    });

    ['cart:refresh', 'cart:update', 'theme:cart:update'].forEach(eventName => {
      document.addEventListener(eventName, async () => {
        const config = getConfig();
        if (!config || !config.autoHideOnThreshold) return;

        const mittCount = await getTotalMittCount();
        if (mittCount >= config.bundleThreshold) {
          document.querySelectorAll('.cart-item-upgrade').forEach(el => {
            animateUpgradeRemoval(el, config);
          });
        }
      });
    });

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const dialog = mutation.target;
          if (dialog.hasAttribute('open')) {
            clearCartCaches();
          }
        }
      });
    });

    const cartDialog = document.querySelector('.cart-drawer__dialog');
    if (cartDialog) {
      observer.observe(cartDialog, {
        attributes: true,
        attributeFilter: ['open']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.cartUpgradeHandler = {
    state,
    getTotalMittCount,
    clearCartCaches
  };
})();
